import React, { useRef, useState } from 'react';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import './Contact.css';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  reference_code: '' // hidden from people; bots fill it in (see server.js)
};

const Contact = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  // When the form appeared; the server ignores submissions made in under 3 s
  const startedAt = useRef(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear status when user starts typing
    if (submitStatus.message) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, elapsedMs: Date.now() - startedAt.current }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Thank you for your message! We will get back to you soon.'
        });
        setFormData(EMPTY_FORM);
        startedAt.current = Date.now();
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Unable to connect to server. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FiMapPin />,
      title: 'Our Location',
      details: [' 499 Main St S, Brampton, ON L6Y 1N7, Canada']
    },
    {
      icon: <FiPhone />,
      title: 'Phone Number',
      details: ['+1 647-643-7182']
    },
    {
      icon: <FiMail />,
      title: 'Email Address',
      details: ['support@sempersyscorp.com']
    },
    {
      icon: <FiClock />,
      title: 'Working Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat - Sun: Closed']
    }
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className="section-header">
          <span className="section-subtitle">Contact Us</span>
          <h2 className="section-title">Let's Start a Conversation</h2>
          <p className="section-description">
            Ready to transform your business with technology? Get in touch with our experts today.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            {contactInfo.map((info, index) => (
              <div key={index} className="info-card">
                <div className="info-icon">{info.icon}</div>
                <div className="info-content">
                  <h3 className="info-title">{info.title}</h3>
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="info-detail">{detail}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <textarea
                name="message"
                placeholder="Your Message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>
            <div className="form-hp" aria-hidden="true">
              <label htmlFor="reference_code">Leave this field empty</label>
              <input
                type="text"
                id="reference_code"
                name="reference_code"
                tabIndex={-1}
                autoComplete="off"
                value={formData.reference_code}
                onChange={handleChange}
              />
            </div>
            {submitStatus.message && (
              <div className={`form-status ${submitStatus.type}`}>
                {submitStatus.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                <span>{submitStatus.message}</span>
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'} {!isSubmitting && <FiSend />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
